import type { AddressList } from '@qbe/dfc-services';
import type { AddressSearchValue } from '@qbe/dfc-smart-ui';
import { AddressType } from './addressTypes';
import type { AddressFieldMap, AddressUpdateMap } from './addressTypes';

// ─── Label helper ─────────────────────────────────────────────────────────────

const ADDRESS_LABELS: Record<AddressType, string> = {
  [AddressType.Postal]: 'Postal Address',
  [AddressType.RepPostal]: 'Residential Address',
  [AddressType.Physical]: 'Postal Address',
};

export const getAddressLabel = (type: AddressType): string => {
  return ADDRESS_LABELS[type];
};

// ─── Field maps per address type ─────────────────────────────────────────────

export const ADDRESS_FIELD_MAPS: Record<AddressType, AddressFieldMap> = {
  [AddressType.Physical]: {
    fieldName: 'cPostalAddress',
    address: 'cPostalAddress',
    addressObject: 'cPostalAddressObject',
    streetAddress: 'pStreetAddress',
    suburb: 'pSuburb',
    state: 'pState',
    postcode: 'pPostcode',
    country: 'pCountry',
    isManualAddress: 'isManualAddressPhy',
    isAddressValid: 'isPhyAddressValid',
  },
  [AddressType.Postal]: {
    fieldName: 'postalAddress',
    address: 'postalAddress',
    addressObject: 'postalAddressObject',
    streetAddress: 'streetAddress',
    suburb: 'suburb',
    state: 'state',
    postcode: 'postcode',
    country: 'country',
    isManualAddress: 'isManualAddressEnabled',
    isAddressValid: 'isPostalAddressValid',
  },
  [AddressType.RepPostal]: {
    fieldName: 'residentialAddress',
    address: 'residentialAddress',
    addressObject: 'residentialAddressObject',
    streetAddress: 'rStreetAddress',
    suburb: 'rSuburb',
    state: 'rState',
    postcode: 'rPostcode',
    country: 'rCountry',
    isManualAddress: 'isManualAddressRes',
    isAddressValid: 'isResAddressValid',
  },
};

export const buildAddressLookupUpdates = (
  type: AddressType,
  addressList: AddressList | null,
  structured: AddressSearchValue | null
): AddressUpdateMap => {
  const f = ADDRESS_FIELD_MAPS[type];

  return {
    [f.address]: addressList?.partialAddress ?? null,
    [f.addressObject]: addressList ?? null,
    [f.streetAddress]: [structured?.buildingName, structured?.streetAddress1]
      .filter((str) => str && str.length)
      .join(', '),
    [f.suburb]: structured?.city ?? '',
    [f.state]: structured?.state ?? '',
    [f.postcode]: structured?.postcode ?? '',
    [f.country]: structured?.country ?? 'Australia',
    [f.isManualAddress]: false,
    [f.isAddressValid]: !!structured,
  };
};

// ─── Write on manual entry ────────────────────────────────────────────────────

export const buildAddressManualUpdates = (
  type: AddressType,
  structured: AddressSearchValue | null
): AddressUpdateMap => {
  const f = ADDRESS_FIELD_MAPS[type];

  return {
    [f.address]: null,
    [f.addressObject]: null,
    [f.streetAddress]: structured?.streetAddress1 ?? '',
    [f.suburb]: structured?.city ?? '',
    [f.state]: structured?.state ?? '',
    [f.postcode]: structured?.postcode ?? '',
    [f.country]: structured?.country ?? '',
    [f.isManualAddress]: true,
    [f.isAddressValid]: false,
  };
};
